import React, { useRef, useState } from "react";
import * as Mui from "@mui/material";
import { FormField } from "component/shared/form-fields";
import { PageVariableType } from "type/page-variable";

interface Props {
	id: string;
	label: string;
	value: string;
	onChange: (value: string) => void;
	/** The page type's variables. Typing "{" lists them, as the old SEO Pages editor did. */
	variables: PageVariableType[];
	multiline?: boolean;
	rows?: number;
}

/**
 * A FormField with the old SEO Pages variable picker: type "{" and choose a variable to insert
 * "{id}" at the cursor.
 */
export const VariableField: React.FC<Props> = ({ id, label, value, onChange, variables, multiline, rows }) => {
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
	const [open, setOpen] = useState(false);

	const insert = (variable: PageVariableType) => {
		setOpen(false);
		const input = inputRef.current;
		// The "{" is already typed, so the variable and the closing brace go in after the cursor.
		const position = input && input.selectionStart !== null ? input.selectionStart : value.length;
		const next = value.substring(0, position) + variable.id + "}" + value.substring(position);
		onChange(next);
		const cursor = position + variable.id.length + 1;
		window.requestAnimationFrame(() => {
			if (inputRef.current) {
				inputRef.current.focus();
				inputRef.current.setSelectionRange(cursor, cursor);
			}
		});
	};

	return (
		<>
			<FormField
				id={id}
				inputRef={inputRef}
				label={label}
				value={value}
				multiline={multiline}
				rows={rows}
				onChange={(event) => onChange(event.target.value)}
				onKeyUp={(event) => {
					if (event.key === "{" && variables.length > 0) {
						setOpen(true);
					}
				}}
				hint={variables.length > 0 ? "Type \"{\" to insert a variable" : undefined}
				spellCheck={false}
			/>
			<Mui.Popover
				open={open}
				anchorEl={inputRef.current}
				onClose={() => setOpen(false)}
				anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
				transformOrigin={{ vertical: "top", horizontal: "left" }}
			>
				<Mui.List dense aria-label={`${label} variables`}>
					{variables.map((variable) => (
						<Mui.ListItemButton key={variable.id} onClick={() => insert(variable)}>
							<Mui.ListItemText primary={variable.label} secondary={`{${variable.id}}`} />
						</Mui.ListItemButton>
					))}
				</Mui.List>
			</Mui.Popover>
		</>
	);
};
